import get from "lodash/get";
import has from "lodash/has";
import last from "lodash/last";
import Bugsnag from "@bugsnag/js";
import * as Quiz from "lib/models/Quiz";
import * as QuizTaken from "lib/models/QuizTaken";
import { getByCategory as getProductsByCategory } from "lib/models/Product";
import filterByQuizSpecifications from "./quizSpecifications";
import getAllSpecValues from "./allSpecValues";
import { getBudgetWeighting, getContractWeighting } from "./budgetWeighting";
import getProductValues from "./productValues";
import getFinalResults from "./results";

/**
 * Generates Quiz Results
 */

const quizResults = async (id, state) => {
  try {
    // Get the last answer, to determine the endQuiz
    const lastAnswer = last(state.answers);

    await QuizTaken.setCompleted(id, {
      endTime: new Date(),
      endQuiz: lastAnswer.quiz,
      tag: state.tag,
      answers: state.answers,
    });

    // Gather necessary data
    // Get all answers for each quiz involved
    state.quizzes = await Promise.all(state.quizzes.map(getQuizAnswers));

    // Get all non-archived products
    let products = await getProductsByCategory(state.quiz.category);

    // Filter Products that don't have a price, or stock, in user's country
    const showOutOfStock = get(state, "quiz.showOutOfStock", false);

    const countryProducts = filterProductsByCountry(
      products,
      state.country,
      showOutOfStock
    );

    // If no products for this country, calculate based upon US
    if (!countryProducts.length) {
      state.country = "US";
      products = filterProductsByCountry(
        products,
        state.country,
        showOutOfStock
      );
    } else {
      products = countryProducts;
    }

    // Filter by Quiz Specifications
    products = await filterByQuizSpecifications(products, state);

    // Get "All Spec Value" for Products
    products = getAllSpecValues(products, state);

    // Get "Contract Weighting" for Products
    products = getContractWeighting(products, state);

    // Get "Budget Weighting" for Products
    products = getBudgetWeighting(products, state);

    // Get "Product Value" for Products
    products = getProductValues(products, state);

    // Fetch Best Choice, Upgrade Option, Budget Option
    let [best, upgrade, budget, relevance] = getFinalResults(products, state);

    // Check onlyOneResult Flag
    const onlyOneResult = get(state, "quiz.onlyOneResult", false);
    if (onlyOneResult) {
      upgrade = null;
      budget = null;
    }

    await QuizTaken.update(id, {
      products: !best ? null : { best, upgrade, budget },
      relevance,
    });

    return id;
  } catch (error) {
    console.error(error);
    Bugsnag.notify(error);
    return null;
  }
};

export default quizResults;

async function getQuizAnswers(quiz) {
  const answers = await Quiz.getAnswers(quiz.id);
  return Promise.resolve({ ...quiz, answers });
}

function filterProductsByCountry(products, country, showOutOfStock = false) {
  return products.filter((product) => {
    // Check product has price
    if (has(product, `prices.${country}.VALUE`)) {
      // Ignore product stock if flag is set
      if (showOutOfStock) return true;

      // Check product's stock status
      return get(product, `prices.${country}.INSTOCK`, true) !== false;
    }

    return false;
  });
}
